// SPDX-License-Identifier: GPL-3.0-or-later
// Frame rules for the render layer. One place for the axis swap.
//
// The table frame has x to the right, y down the playfield, z up from the playfield.
// The three.js frame has x to the right, y up, z toward the player.
// The swap is: three.x = table.x, three.y = table.z, three.z = table.y.
// A table angle of A degrees about the up axis is a three.js rotation.y of -A degrees.

import type { Object3D } from "three";
import type { TableSpec } from "../shared/contracts.ts";

/** The device pixel ratio never goes above this value. A high ratio costs fill rate and gives little. */
export const MAX_PIXEL_RATIO = 2;

/** A light insert or a label lies this far above the playfield. This stops z fighting. */
export const INSERT_LIFT = 0.4;

/** Put an object at a table position. */
export function placeAt(obj: Object3D, x: number, y: number, z: number): void {
  obj.position.set(x, z, y);
}

/** Table base height. */
export function tableHeight(table: TableSpec): number {
  return table.info.tableHeight ?? 0;
}

/**
 * Height of the surface that an item stands on. An unknown or unset name gives the
 * playfield height. A wall gives its top height.
 */
export function surfaceHeight(table: TableSpec, szSurface: string | undefined): number {
  const base = tableHeight(table);
  if (szSurface === undefined || szSurface === "") return base;
  const wall = table.walls.find((w) => w.name === szSurface);
  return wall ? base + wall.heightTop : base;
}

/** Clamp a pixel ratio to a safe range. A bad value gives 1. */
export function capPixelRatio(ratio: number): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return 1;
  return Math.min(ratio, MAX_PIXEL_RATIO);
}

/** Convert degrees to radians. */
export function rad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}
